// src/services/goalProgress.js
//
// The two monthly goal circles on the Coordinator page — Enrollments and
// Screenings — and their leaderboards. Both work the same way: per-period
// counts typed in by hand per coordinator (coordinatorEnrollments /
// coordinatorScreenings, plus the weekly collections), summed for the month
// and compared with that month's goal.
//
// The one difference is the goal default. Enrollments keep their long-standing
// default of 12 when no goal was saved. Screenings have none: until an admin
// sets one, the circle says "Goal not set" rather than show a made-up percent.
//
// Pure and Firebase-free so it is unit-tested (goalProgress.test.js).

export const GOAL_METRICS = Object.freeze({
  enrollments: { id: "enrollments", label: "Randomizations", singular: "randomization" },
  screenings: { id: "screenings", label: "Screenings", singular: "screening" },
});

const DAY_MS = 1000 * 60 * 60 * 24;

/**
 * The goal for a month from its goal document (`exists`, `data`).
 *   enrollments: the saved goal, or 12 when missing/zero (unchanged behaviour)
 *   screenings:  the saved goal when above 0, else null ("Goal not set")
 */
export const goalFromDoc = (metric, exists, data) => {
  if (metric === "screenings") {
    const g = Number(data?.goal);
    return exists && Number.isFinite(g) && g > 0 ? Math.round(g) : null;
  }
  return exists ? Math.max(Number(data?.goal) || 12, 0) : 12;
};

/**
 * One row per leaderboard coordinator, in roster order, with their count for
 * the period (0 when nobody wrote one). Counts for people not on the
 * leaderboard (inactive, or excluded) are left out, as they always were.
 */
export const mergeLeaderboard = (docs, names) => {
  const byName = new Map((docs || []).map((d) => [d.name, Number(d.count) || 0]));
  const rows = (names || []).map((name) => ({ name, count: byName.get(name) || 0 }));
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  return { rows, total };
};

/** Percent of goal, capped at 100. A missing or zero goal gives null. */
export const goalPct = (total, goal) => {
  const g = Number(goal);
  if (!Number.isFinite(g) || g <= 0) return null;
  return Math.min(100, Math.round(((Number(total) || 0) / g) * 100));
};

/** Ranked leaderboard: counts above 0, highest first; ties keep roster order. */
export const rankLeaderboard = (rows) =>
  (rows || [])
    .map((r, i) => ({ ...r, i }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count || a.i - b.i)
    .map(({ i, ...r }) => r);

/**
 * Days left in the month being VIEWED. It used to use the real date even when
 * a past month was selected, so August showed "23 days left" in September.
 *   { kind: "current", days } | { kind: "past" } | { kind: "future" }
 */
export const daysLeftInfo = (monthId, now = new Date()) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(monthId || ""));
  const today = now instanceof Date ? now : new Date(now);
  if (!m) return { kind: "current", days: 0 };
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const viewed = year * 12 + month;
  const current = today.getFullYear() * 12 + today.getMonth();
  if (viewed < current) return { kind: "past" };
  if (viewed > current) return { kind: "future" };
  // Same arithmetic the page always used for the current month.
  const endOfMonth = new Date(year, month + 1, 0);
  return { kind: "current", days: Math.max(Math.ceil((endOfMonth - today) / DAY_MS), 0) };
};

/**
 * Past-months list for the history pop-up, newest first, future months left
 * out. Totals use the same rule as the circle (sum over today's leaderboard
 * roster), so a row always matches what selecting that month shows.
 *
 *   monthIds:       ["2026-09", ...]
 *   docsByMonth:    { enrollments: { "2026-09": [{name,count}] }, screenings: {...} }
 *   goalsByMonth:   { enrollments: { "2026-09": 12 }, screenings: { "2026-09": null } }
 *   closedByMonth:  { "2026-09": {...} }
 */
export const buildGoalHistory = ({
  monthIds = [],
  docsByMonth = {},
  goalsByMonth = {},
  closedByMonth = {},
  names = [],
  currentMonthId,
}) =>
  [...new Set(monthIds)]
    .filter((id) => /^\d{4}-\d{2}$/.test(id) && (!currentMonthId || id <= currentMonthId))
    .sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))
    .map((monthId) => {
      const metric = (id) => {
        const { total } = mergeLeaderboard(docsByMonth[id]?.[monthId], names);
        const goal = goalsByMonth[id]?.[monthId] ?? null;
        return { total, goal, pct: goalPct(total, goal) };
      };
      return {
        monthId,
        closed: !!closedByMonth[monthId],
        enrollments: metric("enrollments"),
        screenings: metric("screenings"),
      };
    });
